import React from 'react';
import { LegalPageShell, Section, Callout } from '../components/shared/LegalPageShell';

/**
 * ContactPage — Stage 20B+ Wave A · /contact
 *
 * Reassures the visitor that a real person reads every message. No form
 * (the reply rate on mailto: is always higher than a contact form, and
 * a form implies a ticketing pipeline we don't currently run).
 */

const NAVY = '#0E1C2E';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const MailtoCard = ({ subject, description, testid }) => (
  <a
    href={`mailto:hello@kindraai.dev?subject=${encodeURIComponent(subject)}`}
    className="block rounded-2xl p-6 md:p-7 transition-colors hover:bg-[#FBFAF6]"
    style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
    data-testid={testid}
  >
    <p
      className="font-sans text-[11px] md:text-[11.5px] uppercase font-medium mb-2"
      style={{ color: TEAL, letterSpacing: '0.18em' }}
    >
      {subject}
    </p>
    <p
      className="font-serif text-[18px] md:text-[20px] leading-[1.35] tracking-[-0.005em] mb-2"
      style={{ color: NAVY }}
    >
      hello@kindraai.dev
    </p>
    <p
      className="font-sans text-[13.5px] md:text-[14px] leading-[1.6]"
      style={{ color: NAVY_SOFT }}
    >
      {description}
    </p>
  </a>
);

const ContactPage = () => (
  <LegalPageShell
    eyebrow="Contact"
    title="Talk to a real person."
    subtitle="Kindra is a small, focused team. Every message you send is read by a person — usually within one business day, often sooner."
    current="/contact"
  >
    <div className="max-w-[680px] mx-auto grid gap-4 md:gap-5" data-testid="contact-mailto-grid">
      <MailtoCard
        subject="Support"
        description="Something not working? Can't find what you're looking for? Got a question about your account or a report you've uploaded."
        testid="contact-support"
      />
      <MailtoCard
        subject="Billing"
        description="Questions about your trial, your subscription, or an unexpected charge. We'll sort it out."
        testid="contact-billing"
      />
      <MailtoCard
        subject="Feedback"
        description="Something confusing? Something we got wrong? Something you wish Kindra did? We want to hear it."
        testid="contact-feedback"
      />
      <MailtoCard
        subject="Security"
        description="Found a security issue? Please include steps to reproduce — we take every report seriously and respond within two business days."
        testid="contact-security"
      />
      <MailtoCard
        subject="Press or partnerships"
        description="Writing about Kindra, or building something that would make it better for people? Let's talk."
        testid="contact-press"
      />
    </div>

    <div className="h-10 md:h-14" />

    <Callout testid="contact-crisis-callout">
      <p className="font-medium mb-2" style={{ color: NAVY }}>If you're in crisis, please don't email.</p>
      <p>
        Kindra is not an emergency service. If you're experiencing a medical emergency, call <strong>000</strong> in Australia or your local emergency number. If you're in emotional distress, Lifeline is available 24/7 on <strong>13 11 14</strong>.
      </p>
    </Callout>

    <Section title="Who replies?">
      <p>
        Small team. Michael (founder) answers most messages personally during the invite phase. No canned replies, no ticket queues, no "your feedback has been logged."
      </p>
    </Section>

    <Section title="Where we're based">
      <p>
        Brisbane, Queensland, Australia.
      </p>
    </Section>
  </LegalPageShell>
);

export default ContactPage;
